import CommonCssClasses from 'bloko/common/constants/commonCssClasses';

/**
 * Возвращает HTML-элемент
 * @param {HTMLElement | jQuery} el
 * @return {HTMLElement}
 */
const getElement = (el) => {
    let element;

    if (window.Node && el instanceof window.Node) {
        element = el;
    } else if (el && typeof el.get === 'function' && el.get(0)) {
        element = el.get(0);
    }

    return element;
};

function isPointInRectangle(x, y, rectangle) {
    const errorDirections = [];

    if (x < rectangle.left) {
        errorDirections.push('left');
    }

    if (x > rectangle.right) {
        errorDirections.push('right');
    }

    if (y < rectangle.top) {
        errorDirections.push('top');
    }

    if (y > rectangle.bottom) {
        errorDirections.push('bottom');
    }

    return errorDirections;
}

export default {
    getElement,
    /**
     * Возвращает размер элемента и его позицию относительно окна
     * @param {HTMLElement} element
     * @return {nsIDOMClientRect | Object} nsIDOMClientRect, в противном случае пустые метрики элемента
     * {width, height, left, top, right, bottom}
     */
    getBoundingClientRect(element) {
        let boundingClientRect;

        // IE mobile кидает ошибку если элемент не в потоке.
        try {
            boundingClientRect = element.getBoundingClientRect();
        } catch (empty) {
            boundingClientRect = {
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                right: 0,
                bottom: 0,
            };
        }

        return boundingClientRect;
    },

    /**
     * Определяет offset метрики элемента
     * @param {jQuery | HTMLElement} el
     * @returns {Object} метрики элемента {width, height, left, top, right, bottom}
     */
    getMetrics(el) {
        const resultBoundingRects = {};
        const element = getElement(el);

        const boundingClientRect = this.getBoundingClientRect(element);
        resultBoundingRects.width = boundingClientRect.width;
        resultBoundingRects.height = boundingClientRect.height;
        resultBoundingRects.left = boundingClientRect.left + window.pageXOffset;
        resultBoundingRects.top = boundingClientRect.top + window.pageYOffset;
        resultBoundingRects.right = resultBoundingRects.left + resultBoundingRects.width;
        resultBoundingRects.bottom = resultBoundingRects.top + resultBoundingRects.height;

        return resultBoundingRects;
    },

    /**
     * Определяет метрики элемента относительно родителя с position: relative, fixed или absolute
     * @param {jQuery | HTMLElement} el
     * @returns {Object} метрики элемента {width, height, left, top, right, bottom}
     */
    getRelativeMetrics(el) {
        const element = getElement(el);
        let result = {
            top: element.offsetTop || 0,
            left: element.offsetLeft || 0,
        };

        const boundingClientRect = this.getBoundingClientRect(element);

        if (window.SVGElement && element instanceof window.SVGElement) {
            result = {
                left: element.getBBox().x,
                top: element.getBBox().y,
            };
        }

        result.width = boundingClientRect.width;
        result.height = boundingClientRect.height;
        result.right = result.left + result.width;
        result.bottom = result.top + result.height;

        return result;
    },

    /**
     * @returns {Object} метрика документа
     */
    getDocumentMetrics() {
        const result = {
            width: document.body.scrollWidth,
            height: document.body.scrollHeight,
            left: 0,
            top: 0,
        };

        result.right = result.width;
        result.bottom = result.height;

        return result;
    },

    /**
     * @returns {Object} метрика видимой в окне части документа (viewport)
     */
    getViewportMetrics() {
        const result = {
            width: window.innerWidth,
            height: window.innerHeight,
        };

        result.left = window.pageXOffset;
        result.top = window.pageYOffset;

        result.right = result.left + result.width;
        result.bottom = result.top + result.height;

        return result;
    },

    /**
     * @param {Number} x
     * @param {Number} y
     * @param {{left, top, right, bottom}} rectangle
     * @returns {Boolean} находится ли точка с координатами x,y в прямоугольнике rectangle
     */
    isPointInRectangle(x, y, rectangle) {
        return isPointInRectangle(x, y, rectangle).length === 0;
    },

    /**
     * @param {Object} rectangle1
     * @param {Object} rectangle2
     * @returns {boolean} помещается ли rectangle1 целиком в rectangle2
     */
    isRectangleInRectangle(rectangle1, rectangle2) {
        return (
            this.isPointInRectangle(rectangle1.left, rectangle1.top, rectangle2) &&
            this.isPointInRectangle(rectangle1.right, rectangle1.bottom, rectangle2)
        );
    },

    checkIfRectangleInRectangle(rectangle1, rectangle2) {
        return [
            ...new Set([
                ...isPointInRectangle(rectangle1.left, rectangle1.top, rectangle2),
                ...isPointInRectangle(rectangle1.right, rectangle1.bottom, rectangle2),
            ]),
        ];
    },

    getScrollbarWidth() {
        if (document.body.innerWidth > window.innerWidth) {
            return 0;
        }

        const div = document.createElement('div');
        div.classList.add(CommonCssClasses.SCROLL_MEASURE);
        document.body.appendChild(div);
        const scrollbarWidth = div.offsetWidth - div.clientWidth;
        document.body.removeChild(div);

        return scrollbarWidth;
    },

    getWindowScrollHeight() {
        return Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.offsetHeight,
            document.body.clientHeight,
            document.documentElement.clientHeight
        );
    },
};
